/**
 * Google Analytics 4 integration for the Mosaics SPA.
 *
 * We deliberately avoid loading gtag.js at build time / in index.html so
 * that:
 *   1. The Measurement ID stays server-configurable (via GA4_MEASUREMENT_ID
 *      in server/.env → GET /api/config/analytics). No client rebuild
 *      needed to swap or disable tracking.
 *   2. Local-dev without the env var never talks to GA4 — no test pageviews
 *      polluting the production property.
 *
 * Exposes three primitives; wiring lives in App.tsx + AuthGate:
 *   - initAnalytics()             fetch config, inject the gtag <script>.
 *   - trackPageView(path, title)  fire a page_view event for SPA route changes.
 *   - identifyUser(uid, email?)   attach a user_id to subsequent events after login.
 *
 * usePageTracking (hooks/usePageTracking.ts) is the React glue that
 * listens to react-router and calls trackPageView() for a small allowlist
 * of paths.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let measurementId: string | null = null;
let initPromise: Promise<void> | null = null;
let scriptLoaded = false;

/** Lazy init — safe to call multiple times; returns the same promise. */
export function initAnalytics(): Promise<void> {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const res = await fetch('/api/config/analytics');
      if (!res.ok) return; // Silent no-op if server can't be reached.
      const data = (await res.json()) as { ga4MeasurementId: string | null };
      if (!data.ga4MeasurementId) return; // Not configured — skip entirely.
      measurementId = data.ga4MeasurementId;
      injectGtag(measurementId);
    } catch (err) {
      // Never let analytics break the app.
      console.warn('[analytics] init failed:', err);
    }
  })();

  return initPromise;
}

/** Inject the standard gtag boot snippet exactly once. Sends the initial
 *  `config` with send_page_view: false — we drive pageviews explicitly on
 *  route change so SPA nav and the initial paint are treated the same. */
function injectGtag(id: string): void {
  if (scriptLoaded) return;
  scriptLoaded = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // send_page_view: false → we own pageview firing; avoids a double-count
  // for the initial route.
  window.gtag('config', id, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
}

/** Fire a GA4 page_view. path is what shows up as page_path in reports. */
export function trackPageView(path: string, title?: string): void {
  if (!measurementId || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.origin + path,
  });
}

/** Attach a user identity to subsequent events. Passing null clears it
 *  (call on sign-out). Email is NOT sent as user_id (GA4 disallows PII);
 *  Firebase uid is the stable ID we want in reports. */
export function identifyUser(uid: string | null): void {
  if (!measurementId || !window.gtag) return;
  window.gtag('config', measurementId, {
    user_id: uid ?? undefined,
    send_page_view: false,
  });
}
